import { NaturalPersonDto } from "api/actor";
import { PersonDto } from "api/contract";
import { useAppSelector } from "app/hooks";
import { LegalEntityFormFieldNames } from "features/new-actor/enums/legalEntityFormFieldNames";
import { useAffectedQuotesConfirm } from "components/domain/AffectedQuotes";
import { ActorType } from "features/new-actor/enums/actorType";
import { AddressFormFieldNames } from "shared/enums/addressFormFieldNames";
import { mapLegalEntity } from "features/new-actor/mappers/optim/mapLegalEntity";
import { mapNaturalPerson } from "features/new-actor/mappers/optim/mapNaturalPerson";
import { LegalEntityFormValues } from "features/new-actor/types/optim/legalEntityFormValues";
import { NaturalPersonFormValues } from "features/new-actor/types/optim/naturalPersonFormValues";
import { selectReferentialData } from "features/referentials/selectors/selectReferentialDataByKey";
import { RefObject, useCallback, useMemo, useRef } from "react";
import { FormikProps } from "formik";
import { actorsAPI } from "services/swagger-api";
import { FormInitialData } from "types/form/formInitialData";
import { useSubmitInsured } from "./useSubmitInsured";
import { getDetailedChanges } from "shared/utils/getUpdatedFields";
import { toast } from "react-toastify";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { NsKey } from "enums/NsKey";
import { resolveLegalEntityId } from "features/new-actor/utils/resolveLegalEntityId";
import { parseApiError } from "shared/utils/apiValidationErrors";
import { useSubsidiariesSync } from "./useSubsidiariesSync";

export type ActorFormValues = NaturalPersonFormValues | LegalEntityFormValues;

export function mapActorToFormValues(
  person: PersonDto,
  referentialData: any
): FormInitialData<ActorType, Partial<ActorFormValues>> {
  if ('firstName' in person && 'lastName' in person) {
    return {
      [ActorType.Natural]: mapNaturalPerson(person as NaturalPersonDto, referentialData)
    }
  }
  return {
    [ActorType.Legal]: mapLegalEntity(person, referentialData)
  }
}

interface UseSubmitActorFormProps {
  person?: PersonDto;
  postSubmit?: (values: Partial<ActorFormValues>) => void;
  riskId?: string;
  /** Rattachement explicite à une OU (cas admin créant un souscripteur pour une orga cible). */
  plateformeId?: string;
  /** Réf du Formik pour remonter les erreurs de validation du back (400) sur les champs. */
  formikRef?: RefObject<FormikProps<any>> | null;
  /** Legal entity: save the accounting data after saving the entity (financials block enabled). */
  withFinancials?: boolean;
}

/** A legal entity created by the form whose accounting data could not be saved afterwards. */
interface CreatedLegalEntityPendingSync {
  /** `id` of the save response. */
  responseId: string;
  /** Resolved legal entity id, when it could be resolved. */
  legalEntityId?: string;
}

/**
 * The back answered (HTTP 200) but refused the save: `isSuccess` false, or no id. A thrown error is
 * already shown by the submit hooks; this case was silent and left the form open with no message.
 */
const notifyRefusedSave = (response: { errorMessage?: string | null }) => {
  toast.error(response.errorMessage || "L'enregistrement a échoué");
};

export const useSubmitActorForm = ({ person, postSubmit, riskId, plateformeId, formikRef, withFinancials }: UseSubmitActorFormProps) => {
  const { submitNaturalPerson, submitLegalEntity } = useSubmitInsured(formikRef);
  const referentialData = useAppSelector(selectReferentialData);
  const { syncFinancials, getRiskPersonId } = useSubsidiariesSync();
  const { t } = useDynamicTranslation([NsKey.ActorForm]);

  const pendingSubmitRef = useRef<{
    values: Partial<ActorFormValues>;
    initialValues: Partial<ActorFormValues>;
    actorType: ActorType;
  } | null>(null);

  const isEditMode = !!person?.id;

  // Created legal entity whose financials save failed: the next submit replays that save only,
  // saving the entity again would create it twice.
  const pendingCreatedSyncRef = useRef<CreatedLegalEntityPendingSync | null>(null);

  // Id of the saved legal entity to sync against. Undefined (message shown) when it cannot be resolved.
  const resolveSavedLegalEntityId = useCallback(async (
    values: LegalEntityFormValues,
    responseId: string,
    isEdit: boolean
  ): Promise<string | undefined> => {
    try {
      const legalEntityId = await resolveLegalEntityId({
        isEdit,
        valuesId: values.id,
        responseId,
        riskId,
        fetchRiskPersonId: getRiskPersonId
      });
      if (!legalEntityId) {
        toast.error(t('ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.SYNC.LEGAL_ENTITY_NOT_FOUND'));
      }
      return legalEntityId;
    } catch (error) {
      const parsed = await parseApiError(error);
      toast.error(parsed.message || t('ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.SYNC.SAVE_ERROR'));
      return undefined;
    }
  }, [riskId, getRiskPersonId, t]);

  // Runs once the legal entity is saved. False: postSubmit must not run, the form stays open.
  const syncLegalEntityFinancials = useCallback(async (
    values: LegalEntityFormValues,
    responseId: string,
    isEdit: boolean
  ): Promise<boolean> => {
    const legalEntityId = await resolveSavedLegalEntityId(values, responseId, isEdit);
    return !!legalEntityId && syncFinancials(legalEntityId, values);
  }, [resolveSavedLegalEntityId, syncFinancials]);

  /**
   * Financials of a legal entity created by this form. On failure, remembers the creation so that
   * a retry does not save the legal entity again.
   * Accepted limit: a retry does not resend the company's own fields (name, address…) corrected
   * in between, only the accounting data, which caused the failure.
   */
  const syncCreatedLegalEntity = useCallback(async (
    values: LegalEntityFormValues,
    created: CreatedLegalEntityPendingSync
  ): Promise<boolean> => {
    const legalEntityId = created.legalEntityId
      ?? await resolveSavedLegalEntityId(values, created.responseId, false);
    if (legalEntityId && await syncFinancials(legalEntityId, values)) {
      pendingCreatedSyncRef.current = null;
      return true;
    }
    pendingCreatedSyncRef.current = { responseId: created.responseId, legalEntityId };
    return false;
  }, [resolveSavedLegalEntityId, syncFinancials]);

  const fetchAffectedQuotes = useCallback(async () => {
    if (!person?.id) return undefined;
    try {
      return await actorsAPI.getAffectedQuotesByPersonId({ personId: person.id });
    } catch (error) {
      console.error("Error fetching affected quotes:", error);
      return undefined;
    }
  }, [person?.id]);

  const executeEditSubmit = useCallback(async () => {
    if (!pendingSubmitRef.current) return;

    const { values, actorType } = pendingSubmitRef.current;

    try {
      let response;
      if (actorType === ActorType.Natural) {
        response = await submitNaturalPerson(values as NaturalPersonFormValues, riskId, plateformeId);
      } else {
        response = await submitLegalEntity(values as LegalEntityFormValues, riskId, plateformeId);
      }
      if (response && response.id && !!response.isSuccess) {
        if (withFinancials && actorType === ActorType.Legal
          && !(await syncLegalEntityFinancials(values as LegalEntityFormValues, response.id, true))) {
          return;
        }
        postSubmit?.(values);
      } else if (response) {
        notifyRefusedSave(response);
      }
    } catch (error) {
      console.error(error)
    } finally {
      pendingSubmitRef.current = null;
    }
  }, [submitNaturalPerson, submitLegalEntity, riskId, plateformeId, postSubmit, withFinancials, syncLegalEntityFinancials]);

  const { showConfirmationDialog } = useAffectedQuotesConfirm({
    entityType: 'person',
    fetchAffectedQuotes,
    onConfirm: executeEditSubmit,
    isEditMode
  });

  const editConfig = useMemo(() => {
    if (!person) {
      return undefined;
    }

    return {
      initialValues: mapActorToFormValues(person, referentialData),
      initialData: person,
      onSubmit: async (values: Partial<ActorFormValues>, initialValues: Partial<ActorFormValues>, actorType: ActorType) => {
        pendingSubmitRef.current = { values, initialValues, actorType };

        // Compare only fields that exist in initialValues (ignore form-added fields like googlePlaceId, latitude, longitude)
        const initialKeys = Object.keys(initialValues as Record<string, any>);
        const filteredValues = initialKeys.reduce((acc, key) => {
          acc[key] = (values as Record<string, any>)[key];
          return acc;
        }, {} as Record<string, any>);

        const { hasChanges } = getDetailedChanges(
          initialValues as Record<string, any>,
          filteredValues,
          {
            // null (API) et "" (défaut de champ) ne sont pas des modifications.
            looseEmptyComparison: true,
            // Champs techniques de géolocalisation : le back ne renvoie pas le place_id, donc
            // FormikGoogleLocation géocode l'adresse existante au montage et remplit
            // place_id / lat / lng en arrière-plan. Ce n'est pas une saisie utilisateur.
            excludeFields: [
              AddressFormFieldNames.GooglePlaceId,
              AddressFormFieldNames.Latitude,
              AddressFormFieldNames.Longitude,
              // Accounting data: loaded from the back into the form after it opens (the reference
              // only holds the empty defaults), saved by its own call, and not part of the person
              // record the affected quotes are rebuilt from. It must never trigger the confirmation.
              LegalEntityFormFieldNames.Financials
            ]
          }
        );

        if (hasChanges) {
          // Show confirmation dialog for affected quotes
          await showConfirmationDialog();
        } else {
          // No changes, skip the modal but still execute the submit
          await executeEditSubmit();
        }
      }
    };
  }, [person, referentialData, showConfirmationDialog, executeEditSubmit]);

  const handleSubmit = useCallback(async (values: ActorFormValues, actorType: ActorType) => {
    let response;
    try {
      const pendingCreatedSync = pendingCreatedSyncRef.current;
      if (pendingCreatedSync && withFinancials && actorType === ActorType.Legal) {
        if (await syncCreatedLegalEntity(values as LegalEntityFormValues, pendingCreatedSync)) {
          values.id = pendingCreatedSync.responseId;
          postSubmit?.(values);
        }
        return;
      }
      if (actorType === ActorType.Natural) {
        response = await submitNaturalPerson(values as NaturalPersonFormValues, riskId, plateformeId);
      } else {
        response = await submitLegalEntity(values as LegalEntityFormValues, riskId, plateformeId);
      }
      if (response && response.id && response.isSuccess) {
        if (withFinancials && actorType === ActorType.Legal
          && !(await syncCreatedLegalEntity(values as LegalEntityFormValues, { responseId: response.id }))) {
          return;
        }
        values.id = response.id;
        postSubmit?.(values);
      } else if (response) {
        notifyRefusedSave(response);
      }
    } catch (error) {
      console.error(error)
    }
  }, [submitLegalEntity, submitNaturalPerson, postSubmit, riskId, plateformeId, withFinancials, syncCreatedLegalEntity]);

  return {
    handleSubmit,
    editConfig,
  };
};
